import React, { createContext, useEffect, useState, ReactNode } from 'react';
import { axiosinstance } from '../axiosClient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setEmployeePassword } from '../services/SecureCredentials';
import { useAppDispatch } from '../redux/helper';
import { setCurrentLocationId } from '../redux/locationSlice';

interface Auth {
  accessToken: string;
  refreshToken: string;
}

interface AuthContextType {
  auth: Auth;
  errorMessage: string;
  setAuth: React.Dispatch<React.SetStateAction<Auth>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogin: (
    phoneNumber: string,
    password: string,
  ) => Promise<{
    status: string;
    accessToken?: string;
    refreshToken?: string;
    message?: string;
  }>;
  tokenAssistant: {
    accessToken: string;
    refreshToken: string;
  };
  setTokenAssistant: React.Dispatch<
    React.SetStateAction<{
      accessToken: string;
      refreshToken: string;
    }>
  >;
}

export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType,
);

interface AuthProviderObject {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderObject> = ({ children }) => {
  const [auth, setAuth] = useState<Auth>({
    accessToken: '',
    refreshToken: '',
  });
  const [hasHydratedAuth, setHasHydratedAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenAssistant, setTokenAssistant] = useState<{
    accessToken: string;
    refreshToken: string;
  }>({
    accessToken: '',
    refreshToken: '',
  });
  const dispatch = useAppDispatch();

  // Hydrate auth tokens from storage on cold start so we don't immediately refresh just because memory is empty.
  useEffect(() => {
    let isMounted = true;
    const hydrate = async () => {
      try {
        const [storedAccess, storedRefresh] = await Promise.all([
          AsyncStorage.getItem('accessToken'),
          AsyncStorage.getItem('refreshToken'),
        ]);
        if (!isMounted) return;
        if (storedAccess || storedRefresh) {
          setAuth({
            accessToken: storedAccess || '',
            refreshToken: storedRefresh || '',
          });
          setTokenAssistant({
            accessToken: storedAccess || '',
            refreshToken: storedRefresh || '',
          });
        }
      } catch (e) {
        // noop
      } finally {
        if (isMounted) setHasHydratedAuth(true);
      }
    };
    hydrate();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogin = async (phoneNumber: string, password: string) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await axiosinstance.post(
        'bp_employee_login_information',
        JSON.stringify({
          bp_phone_number: phoneNumber,
          bp_password: password,
        }),
      );
      console.log('employee_login_information', response);
      if (response.data?.status === 200) {
        // Store login password securely for Back Office auto-login usage
        await setEmployeePassword(password);
        const currentLocationId = response.data?.currentLocationId;
        const ownerId = response.data?.ownerId;
        if (currentLocationId) dispatch(setCurrentLocationId(currentLocationId));
        if (
          response.data.message.toLowerCase() === 'new verification code sent'
        ) {
          await AsyncStorage.setItem('userId', ownerId);
          setIsLoading(false);
          return { status: 'verification_required' };
        }

        const accessToken = response.data?.accessToken;
        const refreshToken = response.data?.refreshToken;
        // Persist BOTH tokens so we can boot instantly without an immediate refresh call.
        if (refreshToken)
          await AsyncStorage.setItem('refreshToken', refreshToken);
        if (accessToken) await AsyncStorage.setItem('accessToken', accessToken);
        setTokenAssistant({
          accessToken: accessToken,
          refreshToken: refreshToken,
        });
        setErrorMessage('');
        return { status: 'success', accessToken, refreshToken };
      } else if (response.data.status !== 200) {
        console.log(response.data?.message);
        console.log('response.data', response.data);
        setErrorMessage(`Ooops! ${response.data?.message}`);
        setIsLoading(false);
        return { status: 'error', message: response.data?.message };
      } else {
        console.log('errorMessage');
        setIsLoading(false);
        setErrorMessage(
          'Please ensure your device is connected to the internet ',
        );
        return {
          status: 'error',
          message: 'Please ensure your device is connected to the internet',
        };
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setErrorMessage('Something went wrong. Please try again');
      return { status: 'error', message: 'Something went wrong' };
    } finally {
      // setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        errorMessage,
        setAuth,
        isLoading,
        setIsLoading,
        handleLogin,
        tokenAssistant,
        setTokenAssistant,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
